// frontend/src/pages/Sabesp/index.js
import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { AuthContext } from "../../authContext";
import {
  postChat, getCitation, listSessions, getSessionHistory, postFeedback, postSummaryChat,
  listGroups, createGroup, listGroupDocuments,
} from "./api/sabespClient.js";
import GroupManagerModal from "./components/GroupManagerModal.js";
import Sidebar from "./components/Sidebar.js";
import MessageBubble from "./components/MessageBubble.js";
import InputBar from "./components/InputBar.js";
import DocumentViewer from "./DocumentViewer/index.js";
import "./Sabesp.css";

const SABESP_ASSISTANT_ID = "1144";

const SUGGESTIONS = [
  "Quais os requisitos da NTS 0292 para elaboração de cadastro técnico digital?",
  "Quais as especificações de uma ventosa segundo a NTS 0322?",
  "Como deve ser executada a ligação de esgoto conforme a NTS 0217?",
  "Em que situações o hidrojateamento (Sewer Jet) é aplicado segundo a NTS 0207?",
];

// Perguntas sugeridas quando o usuario esta conversando com um grupo proprio (contratos e documentos enviados por ele).
const GROUP_SUGGESTIONS = [
  "Qual é o objeto e o valor total do contrato?",
  "Quais são os prazos de execução e de vigência?",
  "Quais multas e penalidades estão previstas por atraso ou descumprimento?",
  "Quais são as garantias, as condições de pagamento e as regras de reajuste?",
];

const PROCESSING_POLL_MS = 5000;

function generateId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
}

// Mesma logica de Historico/index.js:191-211 (Iris): o session_id gerado por
// newSessionId() e "{email}-{count}-{ano}-{mes}-{dia} HH:MM:SS.microssegundos" --
// nao ha campo de data separado em /post_list_session, so o proprio session_id
// carrega isso. Sem essa extracao, toda sessao vinda do backend cairia sempre
// em "Mais antigas" no Sidebar.
function parseSessionTimestamp(sessionId) {
  const datePart = (sessionId || "").split(" ")[0];
  const parts = datePart.split("-");
  if (parts.length < 5) return 0;
  const day = parts.pop();
  const month = parts.pop();
  const year = parts.pop();
  const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function mapHistoryToMessages(history) {
  return history.map((m) => {
    if (m.role === "user") {
      return { id: generateId("msg"), role: "user", text: m.content };
    }
    const refs = m.references || {};
    return {
      id: generateId("msg"),
      role: "agent",
      text: m.content,
      status: "completed",
      logsVisible: false,
      logs: [],
      citations: refs.citations || [],
      figures: refs.figures || [],
      error: null,
      feedback: null,
      interactionId: m.interaction_id || null,
    };
  });
}

export default function Sabesp() {
  const { accessToken, email, newSessionId } = useContext(AuthContext);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [promptValue, setPromptValue] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [managingGroupId, setManagingGroupId] = useState(null);
  const [processingCount, setProcessingCount] = useState(0);
  const [notice, setNotice] = useState(null);
  const [openingRef, setOpeningRef] = useState(false);
  const abortControllerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const activeSession = sessions.find((s) => s.id === activeSessionId) || null;
  const messages = activeSession ? activeSession.messages || [] : [];
  const isLoadingHistory = Boolean(activeSession && activeSession.messages === null);

  useEffect(() => {
    if (!email) return;
    listSessions(SABESP_ASSISTANT_ID, email, accessToken)
      .then((idList) => {
        setSessions((prev) => {
          const localOnly = prev.filter((s) => !idList.some((it) => it.session_id === s.id));
          const fromBackend = idList.map((it) => ({
            id: it.session_id,
            name: it.label || "Conversa sem título",
            timestamp: parseSessionTimestamp(it.session_id),
            messages: null,
            hasLabel: Boolean(it.label),
          }));
          return [...localOnly, ...fromBackend];
        });
      })
      .catch((err) => console.error("Falha ao listar sessões do Assistente NTS:", err))
      .finally(() => setSessionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (!email) return;
    listGroups(accessToken)
      .then(setGroups)
      .catch((err) => console.error("Falha ao carregar grupos do Assistente NTS:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Avisa quando o grupo ativo ainda tem arquivos sendo processados em segundo plano (so avisa: nao bloqueia a pergunta).
  useEffect(() => {
    if (!activeGroupId || !email) {
      setProcessingCount(0);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    async function check() {
      try {
        const docs = await listGroupDocuments(activeGroupId, accessToken);
        if (cancelled) return;
        const processing = docs.filter((d) => d.status === "processando").length;
        setProcessingCount(processing);
        if (processing > 0) timer = setTimeout(check, PROCESSING_POLL_MS);
      } catch (err) {
        if (!cancelled) setProcessingCount(0);
      }
    }
    check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, managingGroupId, email]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 7000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const updateSession = useCallback((sessionId, updater) => {
    setSessions((prev) => prev.map((s) => (s.id === sessionId ? updater(s) : s)));
  }, []);

  const selectSession = useCallback(
    (sessionId) => {
      setActiveSessionId(sessionId);
      const target = sessions.find((s) => s.id === sessionId);
      if (!target || target.messages !== null) return;
      getSessionHistory(SABESP_ASSISTANT_ID, sessionId, accessToken)
        .then((history) => {
          // Se o usuario ja mandou mensagem (ou deu feedback) nesta sessao
          // enquanto o historico ainda carregava, `messages` deixou de ser
          // `null` -- nao sobrescrever o que ja foi feito localmente.
          updateSession(sessionId, (s) => (s.messages !== null ? s : { ...s, messages: mapHistoryToMessages(history) }));
        })
        .catch((err) => console.error("Falha ao carregar histórico da sessão:", err));
    },
    [sessions, accessToken, updateSession]
  );

  const updateMessage = useCallback(
    (sessionId, messageId, updater) => {
      updateSession(sessionId, (s) => ({
        ...s,
        messages: (s.messages || []).map((m) => (m.id === messageId ? updater(m) : m)),
      }));
    },
    [updateSession]
  );

  function startNewChat() {
    setActiveSessionId(null);
    setIsRunning(false);
  }

  function deleteSession(sessionId) {
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    if (activeSessionId === sessionId) {
      setActiveSessionId(null);
    }
  }

  async function handleCreateGroup(name) {
    const group = await createGroup(name, accessToken);
    setGroups((prev) => [...prev, group]);
    setActiveGroupId(group.id);
  }

  function handleGroupRenamed(groupId, newName) {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, name: newName } : g)));
  }

  function handleGroupDeleted(groupId) {
    const removed = groups.find((g) => g.id === groupId);
    if (removed) setNotice(`Grupo "${removed.name}" excluído.`);
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
    if (activeGroupId === groupId) setActiveGroupId(null);
    setManagingGroupId(null);
  }

  function ensureSession(firstPromptText) {
    if (activeSession) return activeSession.id;
    const id = newSessionId();
    const name = firstPromptText.length > 25 ? firstPromptText.slice(0, 22) + "..." : firstPromptText;
    setSessions((prev) => [{ id, name, timestamp: Date.now(), messages: [], hasLabel: false }, ...prev]);
    setActiveSessionId(id);
    return id;
  }

  function fillPrompt(text) {
    setPromptValue(text);
  }

  async function sendPrompt() {
    const prompt = promptValue.trim();
    if (!prompt || isRunning) return;

    const needsTitle = !activeSession || activeSession.hasLabel === false;
    const sessionId = ensureSession(prompt);
    const userMessage = { id: generateId("msg"), role: "user", text: prompt };
    const agentMessage = {
      id: generateId("msg"),
      role: "agent",
      text: "",
      status: "running",
      logsVisible: true,
      logs: [
        { text: "Processando consulta na base de conhecimento...", category: "info" },
        { text: "Executando busca vetorial e híbrida nos documentos...", category: "info" },
      ],
      citations: [],
      figures: [],
      error: null,
      interactionId: null,
      feedback: null,
    };

    setSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, messages: [...(s.messages || []), userMessage, agentMessage] } : s))
    );
    setIsRunning(true);
    setPromptValue("");

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const data = await postChat(prompt, accessToken, { sessionId, groupId: activeGroupId, signal: controller.signal });
      updateMessage(sessionId, agentMessage.id, (m) => ({
        ...m,
        status: "completed",
        logsVisible: false,
        text: data.answer_markdown || "",
        citations: data.citations || [],
        figures: data.figures || [],
        interactionId: data.interaction_id || null,
      }));

      if (needsTitle && data.interaction_id) {
        postSummaryChat(
          [
            { role: "user", content: prompt },
            { role: "assistant", content: data.answer_markdown || "" },
          ],
          data.interaction_id,
          sessionId,
          accessToken
        )
          .then((summary) => {
            if (!summary.label) return;
            updateSession(sessionId, (s) => ({ ...s, name: summary.label, hasLabel: true }));
          })
          .catch((err) => console.error("Falha ao gerar título da conversa:", err));
      }
    } catch (err) {
      if (err.name === "AbortError") {
        updateMessage(sessionId, agentMessage.id, (m) => ({ ...m, status: "cancelled", logsVisible: false }));
      } else {
        updateMessage(sessionId, agentMessage.id, (m) => ({
          ...m,
          status: "failed",
          logsVisible: false,
          error: err.message || String(err),
        }));
      }
    } finally {
      setIsRunning(false);
      abortControllerRef.current = null;
    }
  }

  function cancelRun() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }

  async function openCitationById(citationId) {
    if (openingRef) return;
    setOpeningRef(true); // feedback imediato enquanto a referencia e localizada
    try {
      const c = await getCitation(citationId, accessToken);
      setViewer({ documentId: c.document_id, page: c.page_number, citationId });
    } catch (err) {
      console.error(err);
      setNotice("Esta referência não está mais disponível: o documento ou o grupo foi excluído.");
    } finally {
      setOpeningRef(false);
    }
  }

  function openCitation(documentId, page, citationId) {
    setViewer({ documentId, page, citationId });
  }

  function openFigure(documentId, page, figureId) {
    setViewer({ documentId, page, figureId });
  }

  function setFeedback(messageId, value) {
    if (!activeSession) return;
    const message = (activeSession.messages || []).find((m) => m.id === messageId);
    if (!message) return;
    const nextValue = message.feedback === value ? null : value;
    updateMessage(activeSession.id, messageId, (m) => ({ ...m, feedback: nextValue }));
    if (message.interactionId) {
      postFeedback(message.interactionId, nextValue, accessToken).catch((err) =>
        console.error("Falha ao enviar feedback:", err)
      );
    }
  }

  return (
    <div className="sabesp-page">
      <div className="sabesp-body-layout">
        <Sidebar
          sessions={sessions}
          loading={sessionsLoading}
          activeSessionId={activeSessionId}
          onNewChat={startNewChat}
          onSelectSession={selectSession}
          onDeleteSession={deleteSession}
          groups={groups}
          activeGroupId={activeGroupId}
          onSelectGroup={setActiveGroupId}
          onCreateGroup={handleCreateGroup}
          onManageGroup={setManagingGroupId}
        />

        <main className="sabesp-chat-card">
          <div className="sabesp-top-action-bar">
            <div className="sabesp-action-bar-left">
              <div className="sabesp-ia-selector-capsule" title="Modelo de IA em uso">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="9" x2="15" y2="9"></line>
                  <line x1="9" y1="13" x2="15" y2="13"></line>
                  <line x1="9" y1="17" x2="13" y2="17"></line>
                </svg>
                <span className="sabesp-ia-label">IA</span>
                <span className="sabesp-ia-divider">|</span>
                <span className="sabesp-ia-mode">Gemini 2.5 Flash</span>
              </div>
            </div>
          </div>

          <div className="sabesp-messages-container">
            {isLoadingHistory ? (
              <div className="sabesp-history-loading">
                <span className="sabesp-spinner"></span>
                Carregando conversa...
              </div>
            ) : (
              messages.length === 0 && (
                <div className="sabesp-welcome-box">
                  <h2 className="sabesp-welcome-title">Como posso ajudá-lo?</h2>
                  <div className="sabesp-suggestions-row">
                    {(activeGroupId ? GROUP_SUGGESTIONS : SUGGESTIONS).map((text) => (
                      <div className="sabesp-suggestion-pill-card" key={text} onClick={() => fillPrompt(text)}>
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}

            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                token={accessToken}
                onOpenCitationById={openCitationById}
                onOpenCitation={openCitation}
                onOpenFigure={openFigure}
                onFeedback={(value) => setFeedback(message.id, value)}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {openingRef && (
            <div className="sabesp-notice-bar" role="status">
              <span><span className="sabesp-spinner" /> Abrindo referência...</span>
            </div>
          )}
          {notice && (
            <div className="sabesp-notice-bar" role="status">
              {notice}
              <button onClick={() => setNotice(null)} aria-label="Fechar aviso">&times;</button>
            </div>
          )}
          {activeGroupId && processingCount > 0 && (
            <div className="sabesp-processing-banner" role="status">
              <span className="sabesp-spinner"></span>
              {processingCount === 1
                ? "1 arquivo deste grupo ainda está sendo processado."
                : `${processingCount} arquivos deste grupo ainda estão sendo processados.`}{" "}
              As respostas usam apenas os arquivos já prontos.
            </div>
          )}

          <InputBar
            value={promptValue}
            onChange={setPromptValue}
            onSend={sendPrompt}
            onCancel={cancelRun}
            isRunning={isRunning}
          />
        </main>
      </div>

      {viewer && <DocumentViewer {...viewer} token={accessToken} onClose={() => setViewer(null)} />}

      {managingGroupId && (
        <GroupManagerModal
          group={groups.find((g) => g.id === managingGroupId)}
          token={accessToken}
          onClose={() => setManagingGroupId(null)}
          onRenamed={handleGroupRenamed}
          onDeleted={handleGroupDeleted}
        />
      )}
    </div>
  );
}
